// A minimal promise wrapper over IndexedDB.
//
// Deliberately no library: the whole surface needed here is open / replace a
// store's contents / read a store or index, and a dependency for that would be
// more code to audit than this file.
//
// WHY INDEXEDDB AND NOT SQLITE
// The SQLite plugin bundles a prebuilt libsqlcipher.so compiled for 4 KB memory
// pages. Google Play now requires 16 KB alignment, and no amount of build
// configuration can realign someone else's binary — the upload is simply
// rejected. IndexedDB lives in the WebView, so the app ships NO native library
// and the requirement cannot apply. It also means the offline cache can be
// changed by an OTA update, which a native plugin never could.

const DB_NAME = 'smartlife';
const DB_VERSION = 1;

let dbPromise = null;

// Object stores mirror the SQLite tables one-for-one, so the sync and read
// layers keep their shape and stay comparable with the Flutter implementation.
// Each record is { <key columns>, raw: <the untouched server row> } — the same
// "keys for querying, full row preserved" idea as the raw_json column.
export const STORES = {
  device_type: { keyPath: 'device_id' },
  users: { keyPath: 'user_id' },
  user_location_mapping: { keyPath: ['user_id', 'location_id'] },
  location: { keyPath: 'location_id' },
  location_rooms_mapping: { keyPath: ['user_id', 'location_id', 'room_id'] },
  rooms: { keyPath: 'room_id' },
  devices_installed: {
    keyPath: 'id',
    indexes: {
      by_room: 'room_id',
      by_location: 'location_id',
      by_uno: 'unique_number',
    },
  },
  output_details: { keyPath: 'id', indexes: { by_uno: 'unique_number' } },
  input_details: { keyPath: 'id' },
  favourite_devices: { keyPath: 'unique_number' },
  sync_meta: { keyPath: 'key' },
};

export const STORE_NAMES = Object.keys(STORES);

function req(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      STORE_NAMES.forEach((name) => {
        const spec = STORES[name];
        const store = db.objectStoreNames.contains(name)
          ? open.transaction.objectStore(name)
          : db.createObjectStore(name, { keyPath: spec.keyPath });
        Object.entries(spec.indexes || {}).forEach(([idx, path]) => {
          if (!store.indexNames.contains(idx)) store.createIndex(idx, path);
        });
      });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  }).catch((e) => {
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

// Replace several stores' contents in ONE transaction.
//
// Atomicity matters: a failed sync must leave the previous good cache intact
// rather than a half-updated mixture. IndexedDB aborts the whole transaction on
// any error, which gives exactly that.
export async function replaceAll(entries) {
  const db = await openDb();
  const names = entries.map((e) => e.store);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(names, 'readwrite');
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('transaction aborted'));
    entries.forEach(({ store, records }) => {
      const os = tx.objectStore(store);
      os.clear();
      (records || []).forEach((r) => { if (r) os.put(r); });
    });
  });
}

export async function putOne(store, record) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([store], 'readwrite');
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.objectStore(store).put(record);
  });
}

export async function getAll(store) {
  const db = await openDb();
  const tx = db.transaction([store], 'readonly');
  return req(tx.objectStore(store).getAll());
}

export async function getAllByIndex(store, index, value) {
  const db = await openDb();
  const tx = db.transaction([store], 'readonly');
  return req(tx.objectStore(store).index(index).getAll(value));
}

export async function getOne(store, key) {
  const db = await openDb();
  const tx = db.transaction([store], 'readonly');
  return req(tx.objectStore(store).get(key));
}

export async function countOf(store) {
  const db = await openDb();
  const tx = db.transaction([store], 'readonly');
  return req(tx.objectStore(store).count());
}

export async function clearAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAMES, 'readwrite');
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    STORE_NAMES.forEach((n) => tx.objectStore(n).clear());
  });
}
